import { ReactNode, useMemo } from 'react';
import { faCheck, faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { faCloudExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { autoSavedSelector, useReactFlowStore } from 'assets/js/pages/engage/workflows/utils/store';
import * as Tooltip from 'components/tooltip';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentWorkflow } from 'customHooks/useCurrentWorkflow';
import { useCurrentWorkflowSteps } from 'customHooks/useCurrentWorkflowSteps';
import { isoDateFromServer } from 'helpers/dates';
import { first } from 'lodash';
import { DateTime } from 'luxon';

const AutoSaveStatusIndicator = () => {
    const { data: workflow } = useCurrentWorkflow();
    const { autoSaveStatus } = useReactFlowStore(autoSavedSelector);

    if (autoSaveStatus === 'pending') {
        return (
            <div className="flex items-center gap-2 px-4 font-medium">
                <FontAwesomeIcon icon={faSpinnerThird} className="animate-spin text-gray-500" />
                <span className="text-gray-500">Saving</span>
            </div>
        );
    }

    if (autoSaveStatus === 'success') {
        return (
            <AutosaveTooltip>
                <div className="flex items-center gap-2 px-4 font-medium">
                    <FontAwesomeIcon icon={faCheck} className="text-green-500" />
                    <span className="text-green-500">
                        {workflow.has_draft_version ? 'Saved draft' : 'Saved'}
                    </span>
                </div>
            </AutosaveTooltip>
        );
    }

    if (autoSaveStatus === 'error') {
        return (
            <AutosaveTooltip>
                <div className="flex items-center gap-2 px-4 font-medium">
                    <FontAwesomeIcon icon={faCloudExclamation} className="text-red-600" />
                    <span className="text-red-600">Autosave failed</span>
                </div>
            </AutosaveTooltip>
        );
    }
};

const AutosaveTooltip = ({ children }: { children: ReactNode }) => {
    const { data: workflow } = useCurrentWorkflow();
    const { timezone } = useCurrentAccount();
    const { data: workflowSteps } = useCurrentWorkflowSteps({
        version: workflow.has_draft_version
            ? workflow.current_version + 1
            : workflow.current_version,
    });
    const lastSaved = useMemo(() => {
        const firstStep = first(workflowSteps);
        if (!firstStep?.updated_at) return null;

        const updatedAt = isoDateFromServer(firstStep.updated_at).setZone(timezone);
        const isToday = updatedAt.hasSame(DateTime.now(), 'day');

        return (
            <p className="text-xs">
                Last saved{' '}
                {isoDateFromServer(firstStep.updated_at).toLocaleString(DateTime.TIME_SIMPLE)}
                {!isToday ? (
                    <>
                        <br />
                        {isoDateFromServer(firstStep.updated_at).toLocaleString(DateTime.DATE_MED)}
                    </>
                ) : null}
            </p>
        );
    }, [workflowSteps, timezone]);
    return (
        <Tooltip.Root>
            <Tooltip.Trigger>{children}</Tooltip.Trigger>
            <Tooltip.Portal>
                <Tooltip.Content inverse>
                    <Tooltip.Arrow inverse />
                    {lastSaved}
                </Tooltip.Content>
            </Tooltip.Portal>
        </Tooltip.Root>
    );
};

export { AutoSaveStatusIndicator };
